import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box } from '@mui/material';
import Validate from 'app/validation/validation';
import AnalyticsManager from 'app/analytics';
import { colors, spacing } from 'common/theme';
import { TextHot } from 'components/common/TextHot';
import { TextInputHot, MODE } from 'components/common/TextInputHot';
import { ButtonHot } from 'components/common/ButtonHot';
import Recaptcha, { RECAPTCHA_ACTIONS } from 'components/services/google/recaptcha';
import {
  useGetMembershipGeneratorPageQuery,
  useGetMembershipGeneratorCertificatePdfMutation,
  useSendClubMemberCertificatePdfByEmailMutation,
} from 'redux/api/relations.api';
import { executeWithCaptcha, generateTestId } from 'app/functions';
import * as Actions from 'redux/actions';
import * as Popups from 'constants/popup-types';
import { CERTIFICATE_ACTION } from '../../utils/enums';

async function normalizePdfBlob(pdfData) {
  const bytesFromBinaryString = (str) => {
    const bytes = new Uint8Array(str.length);
    for (let i = 0; i < str.length; i += 1) bytes[i] = str.charCodeAt(i) & 0xff;
    return new Blob([bytes], { type: 'application/pdf' });
  };

  const fromBase64 = (base64) => {
    let normalized = base64.replace(/\s/g, '');
    if (!normalized) throw new Error('Invalid PDF payload');
    while (normalized.length % 4 !== 0) normalized += '=';
    try {
      const binary = atob(normalized);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      return new Blob([bytes], { type: 'application/pdf' });
    } catch {
      throw new Error('Invalid PDF base64');
    }
  };

  const parse = async (data) => {
    if (data instanceof Blob) return data;
    if (data instanceof ArrayBuffer) return new Blob([data], { type: 'application/pdf' });
    if (ArrayBuffer.isView(data)) return new Blob([data.buffer], { type: 'application/pdf' });
    if (data && typeof data === 'object') {
      if (data.data instanceof Blob) return data.data;
      if (data.returnValue != null) return parse(data.returnValue);
      if (data.data != null) return parse(data.data);
    }
    const source = String(data || '').trim();
    if (!source) throw new Error('Empty PDF payload');
    if (source.startsWith('%PDF')) return bytesFromBinaryString(source);
    if (source.startsWith('data:')) {
      const res = await fetch(source);
      return res.blob();
    }
    if (/^https?:\/\//i.test(source) || source.startsWith('/')) {
      const res = await fetch(source, { credentials: 'include' });
      return res.blob();
    }
    try {
      return fromBase64(source);
    } catch {
      return bytesFromBinaryString(source);
    }
  };
  return parse(pdfData);
}

function isLikelyIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

async function triggerPdfDownloadOrOpen(pdfData, filename) {
  const blob = await normalizePdfBlob(pdfData);
  const objectUrl = URL.createObjectURL(blob);

  if (isLikelyIOS()) {
    window.location.assign(objectUrl);
    return;
  }

  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
}

export const MembershipCertificateSection = ({ testID }) => {
  const dispatch = useDispatch();
  const userData = useSelector((store) => store.config.UserData);
  const text = useSelector((store) => store.config.Translations);

  const [firstTry, setFirstTry] = useState(true);
  const [form, setForm] = useState({
    id: { val: '', valid: false, rules: ['not_empty', 'id'], errMsg: '' },
    email: { val: '', valid: false, rules: ['should_add_email_if_not_exists'], errMsg: '' },
    full_name: { val: '', valid: false, rules: ['not_empty', 'full_name'], errMsg: '' },
  });

  const { data: membershipPageData } = useGetMembershipGeneratorPageQuery();
  const [membershipCheckPage, setMembershipCheckPage] = useState(membershipPageData);
  const [responseMessage, setResponseMessage] = useState(null);
  const [formStatus, setFormStatus] = useState(false);

  const [getMembershipGeneratorCertificatePdf, { isLoading: isLoadingPdf }] =
    useGetMembershipGeneratorCertificatePdfMutation();
  const [sendClubMemberCertificatePdfByEmail, { isLoading: isSendingEmail }] =
    useSendClubMemberCertificatePdfByEmailMutation();

  const isPdfActionLoading = isLoadingPdf || isSendingEmail;

  useEffect(() => {
    if (membershipPageData) setMembershipCheckPage(membershipPageData);
  }, [membershipPageData]);

  useEffect(() => {
    if (!userData) return;
    const fullName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim();
    const id = `${userData.user_id || ''}`;
    const email = userData.email || '';
    setForm({
      full_name: { ...form.full_name, val: fullName, valid: Validate(fullName, form.full_name.rules).valid, errMsg: Validate(fullName, form.full_name.rules).msg },
      id: { ...form.id, val: id, valid: Validate(id, form.id.rules).valid, errMsg: Validate(id, form.id.rules).msg },
      email: { ...form.email, val: email, valid: Validate(email, form.email.rules).valid, errMsg: Validate(email, form.email.rules).msg },
    });
  }, [userData?.user_id]);

  useEffect(() => {
    if (formStatus) AnalyticsManager.logRequestMembershipCard();
  }, [formStatus]);

  const handleInput = (e) => {
    const { name, value } = e.target;
    const validate = Validate(value, form[name].rules);
    setForm((prev) => ({ ...prev, [name]: { ...prev[name], val: value, valid: validate.valid, errMsg: validate.msg } }));
  };

  const handleActionSubmit = (action) => (e) => {
    e.preventDefault();
    if (!userData) {
      dispatch(Actions.addPopup({ type: Popups.LOGIN, text: text?.['login_to_check_membership'] }));
      return;
    }

    let form_valid = true;
    const new_state = { ...form };
    for (const field in form) {
      const validate = Validate(form[field].val, form[field].rules);
      new_state[field].valid = validate.valid;
      new_state[field].errMsg = validate.msg;
      if (!validate.valid) form_valid = false;
    }
    setForm(new_state);
    setFirstTry(false);

    if (form_valid) {
      handleCertificateAction(form.full_name.val, action);
    }
  };

  const handleCertificateAction = async (fullName, action) => {
    try {

      executeWithCaptcha(async (token) => {
        if (!token) {
          setFormStatus(false);
          setResponseMessage(text?.['recaptcha_error'] || 'אירעה שגיאה באימות, אנא נסה שוב');
          return;
        }
        try {

          if (action === CERTIFICATE_ACTION.EMAIL) {
            const response = await sendClubMemberCertificatePdfByEmail({
              userName: fullName,
              email: form.email.val || userData?.email,
              userId: userData?.user_id,
              'g-recaptcha-response': token 
            }).unwrap();
            setFormStatus(true);
            setResponseMessage(response?.message || membershipCheckPage?.successMessage);
            return;
          }
          const response = await getMembershipGeneratorCertificatePdf({
            userId: userData?.user_id,
            userName: fullName,
            email: form.email.val || userData?.email,
            'g-recaptcha-response': token,
          }).unwrap();
          const filename = `membership-certificate-${String(userData?.user_id || 'member')}.pdf`;

          await triggerPdfDownloadOrOpen(response, filename);

          setFormStatus(true);
          setResponseMessage(text?.['membership_certificate_downloaded_success_message'] || membershipCheckPage?.successMessage);
        } catch (error) {
          setFormStatus(false);
          setResponseMessage(error?.data?.message || error?.data || error?.message || 'אירעה שגיאה');
        }
      }, RECAPTCHA_ACTIONS.GET_CLUB_MEMBER_CERTIFICATE_PDF);
    } catch (error) {
      setFormStatus(false);
      setResponseMessage(error?.data?.message || error?.data || error?.message || 'אירעה שגיאה');
    }
  };

  if (!membershipCheckPage) return null;

  const shouldShowHeaders = !formStatus && !responseMessage;

  return (
    <Box testId={testID} sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: `${spacing.xl}px` }}>
      <Box testId={generateTestId(testID, 'banner-header')} sx={{ p: `${spacing.md}px` }}>
        <TextHot testId={generateTestId(testID, 'banner-header-text')} size="xxxl" bold="bold" color="primary" isLtr={true}>{membershipCheckPage.bannerHeader}</TextHot>
      </Box>
      
      {shouldShowHeaders && (
        <Box testId={generateTestId(testID, 'headers-container')} sx={{ display: 'flex', flexDirection: 'column', gap: `${spacing.sm}px`, mb: 2, textAlign: 'center', alignItems: 'center' }}>
          <TextHot testId={generateTestId(testID, 'header-text')} size="xxxl" bold="bold" color="primary" isLtr={true}>{membershipCheckPage.header}</TextHot>
          <TextHot testId={generateTestId(testID, 'sub-header-text')} size="m" color="secondary" sx={{ maxWidth: 720 }} isLtr={true}>
            {userData ? membershipCheckPage.subHeader : (text?.['login_to_check_membership'] || membershipCheckPage.subHeader)}
          </TextHot>
        </Box>
      )}

      {!formStatus && !responseMessage && (
        <Box component="form" testId={generateTestId(testID, 'form')} onSubmit={handleActionSubmit(CERTIFICATE_ACTION.DOWNLOAD)} sx={{ display: 'flex', flexDirection: 'column', gap: { xs: `${spacing.lg}px`, md: `${spacing.xxl}px` }, mx: 'auto', width: '100%', maxWidth: { xs: 480, md: 560 } }}>
          {userData && (
            <>
              <TextInputHot testId={generateTestId(testID, 'full-name-input')} type="text" name="full_name" label={membershipCheckPage.textFieldName} value={form.full_name.val} onChange={handleInput} error={!firstTry && !form.full_name.valid} helperText={(!firstTry && !form.full_name.valid) ? form.full_name.errMsg : ''} mode={MODE.FORM} />
              <TextInputHot testId={generateTestId(testID, 'email-input')} type="text" name="email" label={membershipCheckPage.textFieldEmail} value={form.email.val} onChange={handleInput} disabled mode={MODE.FORM} />
              <Box testID={generateTestId(testID, 'recaptcha-container')}>
                <Recaptcha isFunction={true} enterprise={true} onReady={() => {}} />
              </Box>
            </>
          )}
          <Box testId={generateTestId(testID, 'buttons-container')} sx={{ mt: 2, display: 'flex', gap: `${spacing.md}px` }}>
            <ButtonHot testId={generateTestId(testID, 'download-button')} type="submit" mode="contained" variant="primary" size="l" fullWidth disabled={isPdfActionLoading}>{membershipCheckPage.btnText}</ButtonHot>
            <ButtonHot testId={generateTestId(testID, 'email-button')} type="button" onClick={handleActionSubmit(CERTIFICATE_ACTION.EMAIL)} mode="contained" variant="primary" size="l" fullWidth disabled={isPdfActionLoading}>{membershipCheckPage?.btnTextEmail || text?.['send_certificate_by_email']}</ButtonHot>
          </Box>
        </Box>
      )}

      {responseMessage && (
        <Box testId={generateTestId(testID, 'response-message-container')} sx={{ p: 3, borderRadius: 2, backgroundColor: formStatus ? colors.palette.green100 : colors.palette.red100, border: `1px solid ${formStatus ? colors.palette.green500 : colors.palette.red500}`, textAlign: 'center' }}>
          <TextHot testId={generateTestId(testID, 'response-message-text')} size={formStatus ? "l" : "m"} color={formStatus ? "primary" : "error"}>{responseMessage}</TextHot>
        </Box>
      )}
    </Box>
  );
};

export default MembershipCertificateSection;